<template>
  <!-- ============================================================
      AI 生成插画 — 主弹窗
  ============================================================ -->
  <el-dialog
    :model-value="modelValue"
    :close-on-click-modal="!generating"
    :close-on-press-escape="!generating"
    :before-close="handleBeforeClose"
    width="520px"
    align-center
    class="ai-generate-dialog"
  >
    <!-- #header 插槽：标题 + 余额，原生关闭按钮在插槽外部 -->
    <template #header>
      <div class="ai-generate-header">
        <span class="ai-generate-title-text">Ai生成插画</span>
        <div class="ai-generate-balance">
          <span class="ai-generate-balance__label">云币余额</span>
          <span class="ai-generate-balance__icon">
            <SvgIcon name="ai-cost" :size="12" />
          </span>
          <span class="ai-generate-balance__value">{{ formattedBalance }}</span>
          <button class="ai-generate-balance__buy" type="button" @click="handleBuyCoin">
            购买
          </button>
        </div>
      </div>
    </template>

    <!-- Body -->
    <div class="ai-generate-body">
      <p class="ai-generate-desc">编辑提示词，可重新生成当前页插画</p>

      <div class="ai-generate-textarea-wrapper">
        <textarea
          ref="textareaRef"
          v-model="promptText"
          class="ai-generate-textarea"
          :placeholder="placeholder"
          @input="handleInput"
        ></textarea>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="ai-generate-footer">
        <button
          v-if="!userStore.isPremiumUser"
          class="ai-generate-upgrade-tip"
          type="button"
          @click="handleUpgrade"
        >
          <span class="ai-generate-upgrade-tip__icon">
            <SvgIcon name="ai-cost" :size="12" />
          </span>
          <span class="ai-generate-upgrade-tip__text">升级会员 立享 7折</span>
        </button>

        <div class="ai-generate-footer-buttons">
          <button
            class="ai-generate-btn ai-generate-btn--cancel"
            type="button"
            :disabled="generating"
            @click="handleCancel"
          >
            取消
          </button>

          <button
            class="ai-generate-btn ai-generate-btn--generate"
            type="button"
            :disabled="generating || !promptText.trim()"
            @click="handleGenerate"
          >
            <template v-if="generating">
              <SvgIcon name="spinner" :size="14" class="ai-generate-spinner" />
              <span>生成中...</span>
            </template>
            <template v-else>
              <span>重新生成</span>
              <span class="ai-generate-btn__icon">
                <SvgIcon name="ai-cost" :size="12" />
              </span>
              <span class="ai-generate-btn__cost">{{ displayCost }}</span>
            </template>
          </button>
        </div>
      </div>
    </template>
  </el-dialog>

  <!-- ============================================================
      云币不足弹窗
  ============================================================ -->
  <InsufficientCoinsModal
    v-model="showInsufficientCoins"
    action-text="生成插画"
    :required-coins="displayCost"
    :current-coins="balanceStore.balance"
    :member-coins="MEMBER_GENERATION_COST"
    @recharge="handleBuyCoin"
    @upgrade="handleUpgrade"
    @closed="handleInsufficientCoinsClosed"
  />

  <!-- ============================================================
      AI 生成进度弹窗（嵌套 el-dialog，Element Plus 自动叠加 z-index）
  ============================================================ -->
  <el-dialog
    :model-value="generating"
    title="AI 生成插画"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    :before-close="handleProgressClose"
    width="400px"
    :align-center="true"
    class="ai-progress-dialog"
  >
    <div class="progress-body">
      <!-- 环形渐变旋转动画 -->
      <svg
        class="spinner-icon"
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g clip-path="url(#ai_progress_spinner_clip)">
          <g transform="matrix(0.03 0 0 -0.03 30 30)">
            <foreignObject x="-1166.67" y="-1166.67" width="2333.33" height="2333.33">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  background: conic-gradient(
                    from 90deg,
                    rgba(99, 36, 235, 1) 0deg,
                    rgba(241, 82, 158, 1) 136.8deg,
                    rgba(255, 255, 255, 0) 360deg
                  );
                  height: 100%;
                  width: 100%;
                  opacity: 1;
                "
              ></div>
            </foreignObject>
          </g>
        </g>
        <path
          d="M30 60C46.5685 60 60 46.5685 60 30C60 13.4315 46.5685 0 30 0C13.4315 0 0 13.4315 0 30C0 46.5685 13.4315 60 30 60ZM30 9.975C41.0473 9.975 50.025 18.9569 50.025 30C50.025 41.0432 41.0473 50.025 30 50.025C18.9527 50.025 9.975 41.0432 9.975 30C9.975 18.9569 18.9527 9.975 30 9.975Z"
          data-figma-gradient-fill="true"
        />
        <circle cx="55" cy="30" r="5" fill="#6324EB" />
        <defs>
          <clipPath id="ai_progress_spinner_clip">
            <path
              d="M30 60C46.5685 60 60 46.5685 60 30C60 13.4315 46.5685 0 30 0C13.4315 0 0 13.4315 0 30C0 46.5685 13.4315 60 30 60ZM30 9.975C41.0473 9.975 50.025 18.9569 50.025 30C50.025 41.0432 41.0473 50.025 30 50.025C18.9527 50.025 9.975 41.0432 9.975 30C9.975 18.9569 18.9527 9.975 30 9.975Z"
            />
          </clipPath>
        </defs>
      </svg>

      <div class="progress-text">
        {{ generationMessage || 'AI 正在生成...'
        }}{{ generationProgress > 0 ? ` ${generationProgress}%` : '' }}
      </div>

      <div class="progress-subtitle">请稍等片刻，勿刷新或关闭网页</div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
  import { computed, ref, watch, nextTick } from 'vue';
  import { ElMessage } from 'element-plus';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import InsufficientCoinsModal from '@/components/common/InsufficientCoinsModal.vue';
  import { useBalanceStore, useUserStore } from '@/store';
  import { useCoinGift, useUpgradeModal } from '@/hooks';
  import { regeneratePageImage } from '@/api/storybook';
  import type { RegeneratePageImagePayload } from '@/api/storybook';

  // ============================================================
  // Props & Emits
  // ============================================================

  const props = defineProps<{
    modelValue: boolean;
    initialPrompt: string;
    storyId: number;
    page: number;
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    generated: [imageUrl: string, prompt: string];
  }>();

  // ============================================================
  // Constants
  // ============================================================

  const GENERATION_COST = 36;
  const BASIC_MEMBER_GENERATION_COST = Math.floor(GENERATION_COST * 0.8);
  const MEMBER_GENERATION_COST = Math.floor(GENERATION_COST * 0.7);

  // ============================================================
  // Stores & Hooks
  // ============================================================

  const balanceStore = useBalanceStore();
  const userStore = useUserStore();
  const { showUpgrade, showCoinBuy } = useUpgradeModal();
  const { checkAndOpenCoinGift } = useCoinGift();

  // ============================================================
  // State
  // ============================================================

  const promptText = ref('');
  const generating = ref(false);
  const generationProgress = ref(0);
  const generationMessage = ref('');
  const showInsufficientCoins = ref(false);
  const textareaRef = ref<HTMLTextAreaElement | null>(null);

  const placeholder = '请描述您想要的插画内容，AI 将根据提示词生成画面...';

  const formattedBalance = computed(() =>
    new Intl.NumberFormat('zh-CN').format(balanceStore.balance)
  );

  /** 根据会员等级计算实际消耗云币 */
  const displayCost = computed(() => {
    const uType = userStore.userData.uType;
    if (uType === 2) return MEMBER_GENERATION_COST;
    if (uType === 1) return BASIC_MEMBER_GENERATION_COST;
    return GENERATION_COST;
  });

  // ============================================================
  // Watchers
  // ============================================================

  watch(
    () => props.modelValue,
    async (visible) => {
      if (visible) {
        promptText.value = props.initialPrompt || '';
        balanceStore.refreshBalance();
        await nextTick();
        textareaRef.value?.focus();
      }
    }
  );

  // ============================================================
  // Methods
  // ============================================================

  function handleInput() {
    const el = textareaRef.value;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }

  function closeModal() {
    if (generating.value) return;
    emit('update:modelValue', false);
  }

  function handleBeforeClose(done: () => void) {
    if (generating.value) return;
    emit('update:modelValue', false);
    done();
  }

  function handleCancel() {
    closeModal();
  }

  function handleBuyCoin() {
    showCoinBuy();
  }

  function handleUpgrade() {
    showUpgrade();
  }

  function handleInsufficientCoinsClosed(reason: 'cancel' | 'recharge' | 'upgrade') {
    if (reason === 'cancel') void checkAndOpenCoinGift();
  }

  function handleProgressClose(done: () => void) {
    if (generating.value) return;
    done();
  }

  async function handleGenerate() {
    if (generating.value || !promptText.value.trim()) return;

    if (balanceStore.balance < displayCost.value) {
      showInsufficientCoins.value = true;
      return;
    }

    generating.value = true;
    generationProgress.value = 0;
    generationMessage.value = 'AI 正在生成...';

    try {
      const payload: RegeneratePageImagePayload = {
        id: props.storyId,
        page: props.page,
        scene_description: promptText.value.trim()
      };

      const result = await regeneratePageImage(payload, {
        intervalMs: 3000,
        timeoutMs: 900000,
        onUpdate(task) {
          generationProgress.value = task.progress ?? 0;
          if (task.message) {
            generationMessage.value = task.message;
          } else if (task.progress > 0) {
            generationMessage.value = `AI 正在生成...`;
          }
        }
      });

      emit('generated', result.image_url, result.scene_description);
      generating.value = false;
      emit('update:modelValue', false);
    } catch (err: any) {
      ElMessage.error(err?.message || '生成异常，请重试');
    } finally {
      generating.value = false;
    }
  }
</script>

<style scoped>
  /* ============================================================
   Body
============================================================ */
  .ai-generate-body {
    padding: 0;
  }

  /* 云币余额 */
  .ai-generate-balance {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 500;
    line-height: 19.5px;
    color: #333333;
  }

  .ai-generate-balance__label {
    white-space: nowrap;
  }

  .ai-generate-balance__icon {
    display: inline-flex;
    align-items: center;
  }

  .ai-generate-balance__value {
    white-space: nowrap;
  }

  .ai-generate-balance__buy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 10px;
    margin-left: 4px;
    border: none;
    border-radius: 66px;
    background: #6033e1;
    color: #ffffff;
    font-size: 14px;
    font-weight: 400;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.2s;
  }

  .ai-generate-balance__buy:hover {
    background: #5128c7;
  }

  .ai-generate-desc {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 500;
    line-height: 19.5px;
    color: #333333;
  }

  .ai-generate-textarea-wrapper {
    border: 1px solid #eff0f7;
    border-radius: 12px;
    background: #ffffff;
    overflow: hidden;
    transition: border-color 0.2s;
  }

  .ai-generate-textarea-wrapper:focus-within {
    border-color: #6033e1;
  }

  .ai-generate-textarea {
    display: block;
    width: 100%;
    min-height: 180px;
    max-height: 200px;
    box-sizing: border-box;
    padding: 10px 16px;
    border: none;
    outline: none;
    resize: none;
    font-family: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.5;
    color: #160211;
    background: transparent;
  }

  .ai-generate-textarea::placeholder {
    color: #94a3b8;
  }

  /* ============================================================
   Footer（el-dialog #footer 插槽内容）
============================================================ */
  .ai-generate-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  /* ============================================================
   Header（#header 插槽内容）
============================================================ */
  .ai-generate-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .ai-generate-title-text {
    font-size: 16px;
    font-weight: 700;
    line-height: 25px;
    color: #160211;
    white-space: nowrap;
  }

  /* 升级会员提示 */
  .ai-generate-upgrade-tip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 8px 12px;
    border: 1px solid rgba(254, 230, 181, 0.18);
    border-radius: 14px;
    background: #fff8ec;
    cursor: pointer;
    margin-right: auto;
    transition: opacity 0.2s;
  }

  .ai-generate-upgrade-tip:hover {
    opacity: 0.8;
  }

  .ai-generate-upgrade-tip__icon {
    display: inline-flex;
    align-items: center;
  }

  .ai-generate-upgrade-tip__text {
    font-size: 11px;
    line-height: 18px;
    color: #d38743;
    white-space: nowrap;
  }

  /* 底部按钮组 */
  .ai-generate-footer-buttons {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .ai-generate-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 40px;
    padding: 0 24px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    cursor: pointer;
    white-space: nowrap;
    transition:
      background-color 0.2s,
      border-color 0.2s,
      opacity 0.2s;
  }

  .ai-generate-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ai-generate-btn--cancel {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #333333;
  }

  .ai-generate-btn--cancel:hover:not(:disabled) {
    background: #f9fafb;
  }

  .ai-generate-btn--generate {
    border: none;
    background: #6033e1;
    color: #ffffff;
  }

  .ai-generate-btn--generate:hover:not(:disabled) {
    background: #5128c7;
  }

  .ai-generate-btn__icon {
    display: inline-flex;
    align-items: center;
    margin-left: 2px;
  }

  .ai-generate-btn__cost {
    font-family: 'ABeeZee', sans-serif;
    font-size: 13px;
    line-height: 14px;
  }

  /* Loading spinner（按钮内） */
  .ai-generate-spinner {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* ============================================================
   Progress Dialog — scoped :deep() 可命中 teleport 后的元素
   （el-dialog 内部 Teleport 将其移至 body，但仍保留 scoped 属性）
============================================================ */
  .ai-progress-dialog :deep(.el-dialog) {
    width: 400px;
    background: linear-gradient(180deg, #f6f9f7 0%, #f6f9f7 100%);
    border-radius: 12px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  }

  .ai-progress-dialog :deep(.el-dialog__header) {
    padding: 32px 32px 0 32px;
    margin: 0;
  }

  .ai-progress-dialog :deep(.el-dialog__title) {
    font-family: 'Source Han Sans CN VF', Inter, sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: #160211;
  }

  .ai-progress-dialog :deep(.el-dialog__headerbtn) {
    top: 32px;
    right: 32px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
  }

  .ai-progress-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
    color: #999999;
    font-size: 12px;
  }

  .ai-progress-dialog :deep(.el-dialog__headerbtn:hover) {
    background: rgba(0, 0, 0, 0.06);
  }

  .ai-progress-dialog :deep(.el-dialog__body) {
    padding: 0 32px !important;
  }

  .ai-progress-dialog :deep(.el-dialog__footer) {
    display: none;
  }

  /* 进度内容区 */
  .progress-body {
    height: 220px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .progress-body .spinner-icon {
    display: block;
    width: 60px;
    height: 60px;
    margin-bottom: 26px;
    flex: 0 0 auto;
    animation: progress-spin 0.9s linear infinite;
  }

  @keyframes progress-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .progress-body .progress-text {
    margin-bottom: 10px;
    font-family: 'Source Han Sans CN VF', Inter, sans-serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 26px;
    color: #160211;
    text-align: center;
  }

  .progress-body .progress-subtitle {
    font-family: 'Source Han Sans CN VF', Inter, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
    color: #6e6a7c;
    text-align: center;
  }
</style>

<style>
  /* ============================================================
   非 scoped — el-dialog 被 Element Plus Teleport 到 body，
   class 落在 .el-dialog 面板上，需要通过它定位。
============================================================ */

  /* 主弹窗面板 */
  .ai-generate-dialog.el-dialog {
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
  }

  .ai-generate-dialog.el-dialog .el-dialog__header {
    padding: 16px 16px 12px !important;
    margin: 0 !important;
  }

  .ai-generate-dialog.el-dialog .el-dialog__body {
    padding: 0 16px !important;
  }

  .ai-generate-dialog.el-dialog .el-dialog__footer {
    padding: 16px 16px 16px !important;
    margin: 0 !important;
  }
</style>
